import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { type Filter, type TradedPosition, type TradedPositionField } from "../context/types"
import { type SelectedInstrument } from "helpers/types/instruments/selected"
import { type AssetViewGroupBy } from "connected-components/asset-view/templates/types"
import { removeInstanceKey } from "./remove-instance-key"

// Production callers (add-trade/renderers/index.jsx) push
// { security, field, value, isNewPosition, filter, base }. `field` is the only
// shape the reducer pattern-matches on (the `activePercentEquity` branch in
// `addTrade`), so it is required; the rest are loosely typed to mirror what the
// JS surface actually constructs.
export interface TradeItem {
  field: string
  base?: SelectedInstrument
  security?: Record<string, unknown>
  value?: number
  isNewPosition?: boolean
  filter?: Filter
  id?: string
}

export interface TradeState {
  trades: TradeItem[]
  tradedPosition: TradedPosition | null
  tradedPositionField: TradedPositionField | null
  actionsBase: SelectedInstrument | null
  isExpanded: boolean
  assetViewGroupBy: AssetViewGroupBy | null
  isPreferencesLoaded: boolean
  /** Bumped by resetTrades/resetTradeContext only (not addTrade) - a stable "user took a corrective action" signal for consumers like the ContributorsDataGrid ErrorBoundary key, as opposed to `trades` which gets a new reference on every add too. */
  resetCount: number
}

/** Keyed by dashboardId. Partial forces TS to narrow absent keys to undefined. */
export type TradeRootState = Partial<Record<string, TradeState>>

export const DEFAULT_TRADE_STATE: TradeState = {
  trades: [],
  tradedPosition: null,
  tradedPositionField: null,
  actionsBase: null,
  isExpanded: false,
  assetViewGroupBy: null,
  isPreferencesLoaded: false,
  resetCount: 0,
}

function ensureInstance(state: TradeRootState, dashboardId: string): TradeState {
  if (!state[dashboardId]) {
    state[dashboardId] = { ...DEFAULT_TRADE_STATE }
  }
  return state[dashboardId] as TradeState
}

export const tradeSlice = createSlice({
  name: "dashboard/trade",
  initialState: {} as TradeRootState,
  reducers: {
    setPreferences(
      state,
      action: PayloadAction<{ dashboardId: string, isExpanded?: boolean }>,
    ) {
      const { dashboardId, isExpanded } = action.payload
      const instance = ensureInstance(state, dashboardId)
      if (isExpanded !== undefined) {
        instance.isExpanded = isExpanded
      }
      instance.isPreferencesLoaded = true
    },

    addTrade(
      state,
      action: PayloadAction<{ dashboardId: string, trade: TradeItem }>,
    ) {
      const { dashboardId, trade } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.trades.push(trade)
      if (trade.base && trade.field === "activePercentEquity" && !instance.actionsBase) {
        instance.actionsBase = trade.base
      }
    },

    setTrades(
      state,
      action: PayloadAction<{ dashboardId: string, trades: TradeItem[], actionsBase: SelectedInstrument | null }>,
    ) {
      const { dashboardId, trades, actionsBase } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.trades = trades
      instance.actionsBase = actionsBase
    },

    resetTrades(
      state,
      action: PayloadAction<{ dashboardId: string }>,
    ) {
      const { dashboardId } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.trades = []
      instance.actionsBase = null
      instance.resetCount = (instance.resetCount ?? 0) + 1
    },

    resetTradeContext(
      state,
      action: PayloadAction<{ dashboardId: string }>,
    ) {
      const { dashboardId } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.trades = []
      instance.tradedPosition = null
      instance.tradedPositionField = null
      instance.actionsBase = null
      instance.assetViewGroupBy = null
      instance.resetCount = (instance.resetCount ?? 0) + 1
    },

    setTradedPosition(
      state,
      action: PayloadAction<{ dashboardId: string, tradedPosition: TradedPosition | null, tradedPositionField: TradedPositionField }>,
    ) {
      const { dashboardId, tradedPosition, tradedPositionField } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.tradedPosition = tradedPosition
      instance.tradedPositionField = tradedPositionField
    },

    setExpanded(
      state,
      action: PayloadAction<{ dashboardId: string, isExpanded: boolean }>,
    ) {
      const { dashboardId, isExpanded } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.isExpanded = isExpanded
    },

    setAssetViewGroupBy(
      state,
      action: PayloadAction<{ dashboardId: string, assetViewGroupBy: AssetViewGroupBy | null }>,
    ) {
      const { dashboardId, assetViewGroupBy } = action.payload
      const instance = ensureInstance(state, dashboardId)
      instance.assetViewGroupBy = assetViewGroupBy
    },

    updateTradeFilters(
      state,
      action: PayloadAction<{ dashboardId: string, filter: Filter }>,
    ) {
      const { dashboardId, filter } = action.payload
      const instance = ensureInstance(state, dashboardId)
      if (instance.trades.length > 0) {
        instance.trades.forEach((trade) => {
          trade.filter = filter
        })
      }
    },

    // Drops the per-dashboard entry on delete (no unmount GC, else it leaks).
    removeInstance(state, action: PayloadAction<{ dashboardId: string }>) {
      return removeInstanceKey(state, action.payload.dashboardId)
    },
  },
})

export const { removeInstance } = tradeSlice.actions
export default tradeSlice.reducer
